import {useMemo} from 'react';

import {Box, styled} from '@mui/material';
import {getYear, format, startOfDay} from 'date-fns';

import {APPT_POOLING_TYPE, APPT_TYPE, CURRENCY_SYMBOL} from './consts';

import {ZmTooltip} from 'Components/ZmTooltip/ZmTooltip';
import {formatPriceTo2Decimal, getApptType, getBGColorByName} from 'Utils';
import DateWithTimeZone from 'Utils/DateWithTimeZone.js';
import zoomPalette from 'zoomPalette';

const formatDay = Object.freeze({
  Sun: 'Sun',
  Mon: 'Mon',
  Tue: 'Tue',
  Wed: 'Wed',
  Thu: 'Thu',
  Fri: 'Fri',
  Sat: 'Sat',
});

/**
   * Reads timeStr in the given originalTimeZone and converts to the local browser timezone
   * @param {string} timeStr 24hr time string in the user's timezone
   * @param {string} originalTimeZone original user's IANA timezone
   * @return {string} timeString rendered in the local browser time
   */
export const formatTime = (timeStr, originalTimeZone) => {
  const dateStr = '1970-01-01T' + timeStr;
  const date = new DateWithTimeZone(dateStr, originalTimeZone);
  return date.toLocaleTimeString(undefined, {timeStyle: 'short'});
};

/**
 * Helper function to properly display the duration
 * @param {Number} durationMinutes
 * @param {Object} t
 * @return {String}
 */
export const renderDuration = (durationMinutes, t) => {
  const result = [];
  const mins = durationMinutes % 60;
  const hrs = Math.floor(durationMinutes / 60);
  if (hrs) {
    result.push(t(t('apptForm.hour', {count: hrs})));
  }
  if (mins) {
    result.push(t(t('apptForm.minute', {count: mins})));
  }
  return result.join(' ');
};

/**
 * Helper function to properly display the recurrence information
 * @param {Array} segmentsRecurrence
 * @param {String} originalTz
 * @return {String}
 */
export const renderRecurrenceInfo = (segmentsRecurrence, originalTz) => {
  const result = [];
  Object.keys(formatDay).forEach((dayKey) => {
    const daySegs = segmentsRecurrence[dayKey];
    let segString = '';
    if (daySegs && daySegs.length > 0) {
      daySegs.forEach((daySeg) => {
        segString += `${formatTime(daySeg.start, originalTz)} - ${formatTime(daySeg.end, originalTz)}`;
      });
      result.push(`${formatDay[dayKey]} ${segString}`);
    }
  });
  return result;
};

const groupBy = (arr, iter) => {
  const res = {};
  arr.forEach((item) => {
    const group = iter(item);
    if (res[group]) {
      res[group].push(item);
    } else {
      res[group] = [item];
    }
  });
  return res;
};

/**
 * Helper function to properly display the custom segments information
 * @param {Array} segments
 * @return {String}
 */
export const renderSegments = (segments) => {
  const result = [];
  const dateGroupedSegments = groupBy(segments, (v) => startOfDay(new Date(v.start)).toISOString());
  Object.keys(dateGroupedSegments).forEach((dayString) => {
    const date = new Date(dayString).toLocaleDateString();
    const formattedSegments = [];
    dateGroupedSegments[dayString].forEach((seg) => {
      const start = new Date(seg.start).toLocaleTimeString(undefined, {timeStyle: 'short'});

      let end = '';
      if (startOfDay(new Date(seg.end)).toISOString() !== dayString) {
        end += new Date(seg.end).toLocaleDateString() + ', ';
      }
      end += new Date(seg.end).toLocaleTimeString(undefined, {timeStyle: 'short'});
      formattedSegments.push(`${start} - ${end}`);
    });
    result.push([date, formattedSegments.join(', ')].join(', '));
  });
  return result.join(', ');
};

/**
 * Helper function to properly display the custom segments information
 * ex output: Mon Mar 28th (1 timeframes), Thu Jan 26th (3 timeframes)
 * @param {Array} segments
 * @return {String}
 */
export const renderSegmentsByDay = (segments) => {
  const result = [];
  const dateGroupedSegments = groupBy(segments, (v) => startOfDay(new Date(v.start)).toISOString());
  Object.keys(dateGroupedSegments).forEach((dayString) => {
    const date = new Date(dayString);
    const formattedDate = format(date, 'EE MMM do');
    result.push(`${formattedDate} (${dateGroupedSegments[dayString].length} timeframes)`);
  });
  return result.join(', ');
};

/**
 * Helper function to properly display the custom segments time range
 * @param {Array} segments
 * @return {String}
 */
export const renderSegsRange = (segments) => {
  if (!segments?.length) {
    console.error('No segment found in this appointment');
    return '';
  }
  const dateFormat = 'EE, MMM do';
  const dateFormatWithYear = 'EE, MMM do, y';
  const startDate = new Date(segments[0].start);
  const endDate = new Date(segments[segments.length-1].end);
  let result = format(startDate,
    getYear(startDate) === getYear(endDate) ? dateFormat : dateFormatWithYear);
  if (startOfDay(startDate).toISOString() !== startOfDay(endDate).toISOString()) {
    result += ` - ${format(endDate, dateFormatWithYear)}`;
  } else {
    result += `, ${getYear(endDate)}`;
  }
  if (segments.length > 1) {
    result += ` (${segments.length} timeframes)`;
  }
  return result;
};

/**
 * Helper function to properly display the custom segments time range
 * @param {Array} segments
 * @return {String}
 */
export const renderSegsPerDay = (segments) => {
  if (!segments?.length) {
    console.error('No segment found in this appointment');
    return '';
  }

  // const segsMap = new Map();
  const dateFormat = 'EE MMM do';
  const dateFormatWithYear = 'EE MMM do, y';
  const startDate = new Date(segments[0].start);
  const endDate = new Date(segments[segments.length-1].end);
  let result = format(startDate,
    getYear(startDate) === getYear(endDate) ? dateFormat : dateFormatWithYear);
  if (startOfDay(startDate).toISOString() !== startOfDay(endDate).toISOString()) {
    result += ` - ${format(endDate,
      getYear(startDate) === getYear(endDate) ? dateFormat : dateFormatWithYear)}`;
  }
  if (segments.length > 1) {
    result += ` (${segments.length} timeframes)`;
  }
  return result;
};

/**
 * Helper function to properly display One off date
 * @param {Array} createDate
 * @return {String}
 */
export const renderCreatedDate = (createDate) => {
  if (!createDate?.length) {
    console.error('No created date found for this appointment');
    return '';
  }
  const dateFormat = 'MMM d';
  const date = new Date(createDate);
  return format(date, dateFormat);
};

export const TinyAvatar = styled(Box)(({theme}) => ({
  height: '28px',
  width: '28px',
  borderRadius: '10px',
  backgroundColor: '#007C7C',
  color: theme.palette.common.white,
  fontSize: '12px',
  fontWeight: '400',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: '0',
  border: '2px solid white',
  position: 'absolute',
  margin: '0 -2px',
  textTransform: 'uppercase',
}));

/**
 * Helper function to render avatar collection
 * @param {*} props
 * @param {Array} props.users
 * @return {ReactElement}
 */
export const AvatarGroup = (props) => {
  const {sx, users: originalUsers} = props;

  const users = useMemo(() => {
    return originalUsers?.filter((item)=>item)?.map((user) => ({
      ...user,
      bgColor: getBGColorByName(user.displayName),
    }));
  }, [originalUsers]);

  if (!users || !users?.length) {
    return null;
  }
  const collapsedUsers = users.length - 5;
  return (
    <Box sx={{
      position: 'relative',
      height: '28px',
      marginLeft: '2px',
      ...sx,
    }}>
      {
        users.slice(0, 5).map((user, i) => {
          const fullName = user.displayName || '';
          const nameParts = fullName.split(' ');
          return (
            <ZmTooltip
              key={user.email || i}
              title={user.displayName}
              arrow
              placement="top"
            >
              <TinyAvatar sx={{
                left: 22*i,
                backgroundColor: user.bgColor,
              }}>
                {nameParts[0][0] || ''}{nameParts[1]?.[0] || ''}
              </TinyAvatar>
            </ZmTooltip>
          );
        })
      }
      {(users.length > 5) &&
        <ZmTooltip
          key="overflow-users"
          title={
            <Box sx={{
              overflowY: 'auto',
              maxHeight: 100,
            }}>
              {users.slice(5).map((user) => {
                return (<Box key={user.email}>
                  {user.displayName}
                </Box>);
              })}
            </Box>
          }
          arrow
          placement="top"
        >
          <TinyAvatar sx={{
            backgroundColor: zoomPalette.gray['20'],
            color: zoomPalette.black,
            left: 22*5,
          }}>
            +{collapsedUsers <= 99 ? collapsedUsers : '99'}
          </TinyAvatar>
        </ZmTooltip>
      }
    </Box>
  );
};

export const getCapacityTypeText = (appointment, apptType, bookedEvent) => {
  const capacity = appointment?.capacity;
  const isOneOff = appointment?.bookingMethod === APPT_TYPE.ONE_OFF;
  const isPoll = appointment?.bookingMethod === APPT_TYPE.POLL;
  // the `appointment` arg can be an appt or bookedEvent(when appt was deleted)
  // we can make sure it's an appt by checking the `appointmentSummary` is undefined
  const isOneTime = bookedEvent?.pendingEventId || (appointment?.appointmentId && !appointment.appointmentSummary);

  let effectiveApptType = apptType || getApptType(appointment);
  if (apptType === APPT_TYPE.MULTI_POOL) {
    // read the effective appt type from the first user pool;
    // if a multi pool has collective + rr it will display as rr
    effectiveApptType = (appointment?.userPools ?? []).some(
      (pool) => pool.poolingType === APPT_POOLING_TYPE.ROUND_ROBIN
    ) ?
      APPT_POOLING_TYPE.ROUND_ROBIN :
      APPT_POOLING_TYPE.COLLECTIVE;
  }
  let type;
  if (isPoll) {
    type = 'polls.availabilityPoll';
  } else if (isOneOff) {
    type = 'common.oneOff';
  } else if (isOneTime) {
    type = 'common.oneTime';
  } else if (effectiveApptType === APPT_TYPE.ROUND_ROBIN) {
    type = 'apptTypeForm.roundRobinTitle';
  } else if (effectiveApptType === APPT_TYPE.COLLECTIVE) {
    type = 'apptTypeForm.collectiveTitle';
  } else if (capacity > 1) {
    type = 'common.oneToMany';
  } else {
    type = 'common.oneToOne';
  }
  return type;
};

export const getPriceText = (appointment, prefix = '') => {
  const price = appointment?.scheduleCost;
  if (price > 0) {
    return `${prefix}${CURRENCY_SYMBOL[appointment?.currency]}${formatPriceTo2Decimal(price)}`;
  }
  return '';
};

export const formatListItemSubtext = (appointment, t, bookers, pending, bookedEvent) => {
  const apptType = getApptType(appointment);
  const capacity = appointment?.capacity;
  const output = [];
  if (appointment?.duration) {
    output.push(renderDuration(appointment.duration, t));
  }
  if (appointment) {
    output.push(t(getCapacityTypeText(appointment, apptType, bookedEvent)));
  }
  if (capacity > 1 && bookers) {
    output.push(t('common.seatsFilled', [bookers.filter((booker) => booker.booker).length, capacity]));
  }
  if (!pending && bookers) {
    if (apptType === APPT_TYPE.COLLECTIVE && bookers.length > 0) {
      const booker = bookers[0]?.displayName;
      const numOtherHosts = appointment?.attendees.filter((item)=>item.host).length - 1;
      if (numOtherHosts === 1) {
        output.push(t('landing.collectiveHost', [booker]));
      } else if (numOtherHosts > 1) {
        output.push(t('landing.collectiveHosts', [booker, numOtherHosts]));
      }
    } else if (bookers.length === 1) {
      output.push(bookers[0]?.displayName);
    }
  }
  // payment amount
  if (bookers?.length === 1 && bookers[0]?.totalFee > 0) {
    output.push(`${bookers[0].currencySymbol}${bookers[0].totalFee}`);
  } else if (appointment?.scheduleCost > 0) {
    output.push(getPriceText(appointment));
  }

  return output.join(', ');
};

export const getListResizeObserver = (columnCountRef, setColumnCount) => {
  return new ResizeObserver((entries) => {
    if (entries && entries.length > 0) {
      const listCon = entries[0];
      const conWidth = listCon?.target?.getBoundingClientRect()?.width;
      if (conWidth > 0) {
        /**
         * A row can have a maximum of four appt cards.
         * If the screen becomes narrower, the number of cards in a row will decrease to 3/2/1 dynamically.
         * The cards in the row will evenly divide the screen width.
         *
         * min width of item is 220px, gap width is 16px
         */
        // 4 cards: 928px [220px * 4 + 16px * 3]
        // 3 cards: 692px [220px * 3 + 16px * 2]
        // 2 cards: 456px [220px * 2 + 16px * 1]
        if (conWidth >= 928) {
          if (columnCountRef.current !== 4) {
            columnCountRef.current = 4;
            setColumnCount(4);
          }
        } else if (conWidth >= 692) {
          if (columnCountRef.current !== 3) {
            columnCountRef.current = 3;
            setColumnCount(3);
          }
        } else if (conWidth >= 456) {
          if (columnCountRef.current !== 2) {
            columnCountRef.current = 2;
            setColumnCount(2);
          }
        } else {
          if (columnCountRef.current !== 1) {
            columnCountRef.current = 1;
            setColumnCount(1);
          }
        }
      }
    }
  });
};

export const getCurrencySymbol = (currency) => {
  return CURRENCY_SYMBOL[currency] || currency;
};

export const getPrice = (appointment, eventInfo) => {
  let price = 0;
  let currency = '';
  if (eventInfo) {
    price = eventInfo.attendees[0].totalFee;
    currency = eventInfo.attendees[0].currency;
  } else {
    price = appointment?.scheduleCost;
    currency = appointment?.currency;
  }
  const currencySymbol = getCurrencySymbol(currency);
  if (price > 0) {
    return `${currencySymbol}${formatPriceTo2Decimal(price)}`;
  }
  return '';
};

export const isPaidAppointment = (appointment) => {
  return appointment?.scheduleCost > 0;
};

